import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import { loadCore } from './lib/core.js';
import { httpsCallable } from 'firebase/functions';
import { analyticsMeasurementId, app, functions, googleAds, metaPixelId } from './lib/firebase';
import { installClientErrorReporting, setClientEventSender } from './lib/clientEvents.js';
import { installAnalytics } from './lib/analytics.js';
import { installAdConversions } from './lib/adConversions.js';
import { installMetaPixel } from './lib/metaPixel.js';
import './styles/tokens.css';
import './styles/app.css';

// There is one light look and no theme to apply (#544). A `coda:theme` key left
// in localStorage is read by nothing, and src/styles/tokens.css has no selector
// it could turn on.

// What only this browser sees reaches the server (#459): uncaught errors and
// unhandled rejections from here on, and the refused writes, stalled queue
// and failed sign-ins the app reports itself. Installed before the core loads
// so a core that fails to load is reported. With no Firebase config there is
// no callable, and every report is dropped.
if (functions) setClientEventSender(httpsCallable(functions, 'reportClientEvent'));
installClientErrorReporting();

// Firebase Analytics, unless declined (#458, #755). These two dynamic imports
// are the only places either SDK is named in the app, so Vite puts them in
// chunks of their own that nothing fetches while denied —
// test/ui/analytics.test.jsx refuses a static import anywhere in src/. With no
// measurement id configured the loaders are never called. src/lib/analytics.js
// has the rules.
installAnalytics({
  app,
  measurementId: analyticsMeasurementId,
  loadAnalytics: () => import('firebase/analytics'),
  loadInstallations: () => import('firebase/installations'),
});

// Google Ads conversions, under the same one answer (#754, #755): gtag.js for
// the Ads id, fetched unless declined, and never with no id configured. Wired
// before the router can touch the address, so the ad's click id in it is
// read first. src/lib/adConversions.js has the rules.
installAdConversions({ ...googleAds, search: window.location.search });

// The Meta Pixel, under the same one answer (#780): fbevents.js fetched unless
// declined, and only in a production build. src/lib/metaPixel.js has the rules.
installMetaPixel({ pixelId: metaPixelId });

// The one await in the app's startup, and the only place the Rust core's
// asynchrony is visible (#214). A component cannot await inside a render, so
// the core is loaded once here; render paths use `coreSync()` in
// src/lib/core.js afterwards.
//
// No fallback renders while waiting: the wait is one fetch of a local asset,
// and no component may render without the rules.
//
// A failure is left to reject: every screen needs the core, so an unhandled
// rejection beats an app pricing lessons at zero. It reaches the server as a
// `client.error` through the listener installed above.
loadCore().then(() => {
  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
});
